import { AssetIcon } from "../assets/Icon.js";
import { GameIcon } from "../assets/gameIcons.js";
import { useEffect, useRef } from "react";
import type { WorldScene } from "./WorldScene.js";
import { useWorld } from "./state.js";
import { useSession } from "../state/session.js";
import { Timer } from "../ui/Timer.js";
import { useT } from "../i18n/index.js";
import { Icon } from "../ui/Icon.js";
import type { BossModelState } from "./WorldBoss.js";
import { activeIncoming, layoutThreatLabels } from "./presentation.js";

/** HTML labels over enclaves (name, HQ, shield/fire) and march columns (action + ETA), projected every frame. */
export function WorldLabels({ scene, bossState, now }: { scene: WorldScene | null; bossState: BossModelState; now: number }) {
  const t = useT();
  const view = useWorld((s) => s.view);
  const me = useWorld((s) => s.me);
  const hidden = useWorld((s) => s.hidden);
  const select = useWorld((s) => s.select);
  const account = useSession((s) => s.account);
  const refs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    if (!scene) return;
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const anchors = scene.anchors();
      const priority = (key: string) => key === `enclave:${account?.id}` ? 0 : key === "incoming:own" ? 1 : key.startsWith("boss:") ? 2 : key.startsWith("march:") ? 3 : 4;
      const captions = anchors.filter((a) => a.visible && priority(a.key) < 4).sort((a, b) => priority(a.key) - priority(b.key)).flatMap((a) => {
        const el = refs.current.get(a.key);
        return el ? [{ ...a, width: el.offsetWidth, height: el.offsetHeight }] : [];
      });
      const positions = layoutThreatLabels(captions, { width: innerWidth, height: innerHeight }, scene.captionObstacles());
      for (const a of anchors) {
        const el = refs.current.get(a.key);
        if (!el) continue;
        const point = positions.get(a.key) ?? a;
        el.style.transform = `translate(-50%, -100%) translate(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px)`;
        el.style.opacity = a.visible ? "1" : "0";
        el.style.visibility = a.visible ? "visible" : "hidden";
        el.style.zIndex = String(Math.round((1 - a.depth) * 500));
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [scene, account?.id]);

  const setRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) refs.current.set(key, el);
    else refs.current.delete(key);
  };
  const enclaves = hidden.has("enclave") ? [] : (view?.tiles.filter((x) => x.kind === "enclave" && x.owner) ?? []);
  const marches = me?.marches.filter((m) => m.state !== "done" && m.action !== "scout") ?? [];
  const bosses = hidden.has("boss") ? [] : view?.tiles.filter((tile) => tile.kind === "boss" && tile.boss && tile.boss.windowUntil > now) ?? [];
  const incoming = activeIncoming(me?.incoming ?? [], now);
  return (
    <div className="labels">
      {enclaves.map((tile) => {
        const own = tile.owner!.id === account?.id;
        const shielded = own ? Math.max(me?.shieldUntil ?? 0, me?.newbieUntil ?? 0) > now : tile.owner!.shielded;
        const burning = own ? (me?.burningUntil ?? 0) > now : tile.owner!.burning;
        return (
          <div key={tile.owner!.id} ref={setRef(`enclave:${tile.owner!.id}`)} className="label" data-testid={own ? "own-enclave-label" : `enclave-label-${tile.owner!.id}`}>
            <button type="button" className={`label-btn map-label ${own ? "own" : ""} ${tile.owner!.alliance ? `relation-${tile.owner!.alliance.relation}` : ""}`} onClick={() => select({ x: tile.x, y: tile.y, tile })}>
              {tile.owner!.alliance ? <span className={`label-tag relation-${tile.owner!.alliance.relation}`} data-testid="enclave-label-tag">{tile.owner!.alliance.tag}</span> : null}
              <span className="ellipsis" style={{ maxWidth: 110 }}>{tile.owner!.name}</span>
              <span className="label-level">{tile.owner!.hq}</span>
              {shielded ? <span title={t("world.shielded")}><GameIcon id="emote.shield" /></span> : null}
              {burning ? <span title={t("world.burning")} data-testid={own ? "own-enclave-burning" : undefined}><GameIcon id="emote.fire" /></span> : null}
            </button>
          </div>
        );
      })}
      {marches.map((m) => (
        <div key={m.id} ref={setRef(`march:${m.id}`)} className="label" data-testid={`march-label-${m.id}`}>
          <button type="button" className={`label-btn map-label march march-select ${m.action}`} onClick={() => useWorld.getState().selectMarch(m.id)}>
            {m.squad?.members[0] ? <AssetIcon id={m.squad.members[0].unitId} portrait size={30} /> : <Icon name="send" />}
            <span><strong>{m.squad?.name || t("world.squad", { n: m.squadSlot })}</strong><small>{t(`world.state.${m.state}`)} · <Timer finishesAt={m.state === "marching" ? m.arrivesAt : (m.endsAt ?? m.arrivesAt)} /></small></span>
          </button>
        </div>
      ))}
      {bosses.map((tile) => <div key={`boss:${tile.x},${tile.y}`} ref={setRef(`boss:${tile.x},${tile.y}`)} className="label world-boss-label" data-testid="world-boss-label" data-state={bossState}>
        <button type="button" className="world-threat-label" onClick={() => select({ x: tile.x, y: tile.y, tile })}>
          <strong>{t("world.boss.titan")}</strong>
          <span className="world-boss-health" role="progressbar" aria-label={t("world.boss.health")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={tile.boss!.hpPct}><i style={{ width: `${Math.max(0, Math.min(100, tile.boss!.hpPct))}%` }} /></span>
          <span>{Math.round(tile.boss!.hpPct)}% <span aria-hidden="true">·</span> <Timer finishesAt={tile.boss!.windowUntil} /></span>
          {bossState === "loading" ? <small>{t("common.loading")}</small> : null}
        </button>
        {bossState === "error" ? <button type="button" className="world-boss-retry" onClick={() => scene?.retryBoss()} data-testid="world-boss-retry">{t("world.boss.loadFailed")} <Icon name="refresh" /> {t("store.retry")}</button> : null}
      </div>)}
      {incoming.length ? <div ref={setRef("incoming:own")} className="label world-incoming-label" data-testid="world-incoming-target">
        <button type="button" className="world-threat-label" onClick={() => useWorld.getState().setPanel("attacks")}>
          <span><Icon name="warning" /> {t("world.incoming", { n: incoming.length })}</span>
          <strong><Timer finishesAt={Math.min(...incoming.map((a) => a.arrivesAt))} /></strong>
        </button>
      </div> : null}
    </div>
  );
}
